import React from 'react';

import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputProps,
  Typography,
} from '@mui/material';

import * as yup from 'yup';
import {useField} from 'formik';
import moment, {Moment} from 'moment';
import {
  DatePicker,
  DateTimePicker,
  StaticDatePicker,
  StaticTimePicker,
} from '@mui/x-date-pickers-pro';

import clsx from 'clsx';
import {
  ActionType,
  DatePickerField,
  DateTimePickerField,
  FieldValue,
  TimePickerField,
} from './AutoFieldNew';
import {getISODateOrEmptyString} from '../../utils/getISODate';
import {AutoFieldDebug} from './AutoFieldDebug';

export enum PickerType {
  date,
  time,
  datetime,
  keyboardDate,
}

const getDateFormat = (
  type: ActionType.datepicker | ActionType.timepicker | ActionType.datetimepicker,
  d: Moment
): string => {
  switch (type) {
    case ActionType.datepicker:
      return d.format('LL');
    case ActionType.timepicker:
      return d.format('LT');
    case ActionType.datetimepicker:
    default:
      return d.format('LLL');
  }
};

export interface AutoDateTimePickerProps extends InputProps {
  field: TimePickerField | DatePickerField | DateTimePickerField;
  controlledValue: string | number | undefined;
  setControlledValue: React.Dispatch<React.SetStateAction<FieldValue | undefined>>;
  handleFieldSubmit: (value?: FieldValue) => void;
  needsValue?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  readOnly: boolean;
}

export function AutoDateTimePicker({
  field,
  controlledValue,
  setControlledValue,
  handleFieldSubmit,
  needsValue,
  helperText,
  readOnly,
  onBlur,
  error,
}: AutoDateTimePickerProps) {
  const [formikField, formikMeta, formikHelpers] = useField(field.id);
  /** if canBeEmpty flag is set, submit empty string to formik instead of undefined */
  const onSubmit = () => {
    const strValue = controlledValue?.toLocaleString();
    if (!strValue) {
      if (field.canBeEmpty && needsValue) return handleFieldSubmit('');
    }
    if (field.type === ActionType.datepicker) {
      // do not provide utc as it will show as the day before
      return handleFieldSubmit(getDateFormat(field.type, moment(strValue)));
    }
    return handleFieldSubmit(getDateFormat(field.type, moment.utc(strValue)));
  };

  const onChange = (d: Moment | null) => {
    if (typeof field.validation === 'function') {
      formikHelpers.setError(field.validation(d));
      if (field.requireSubmit) {
        formikHelpers.setTouched(true, false); // required to show errors in precursor
      }
    } else if (field.validation) {
      field.validation
        ?.validate(d)
        .then(() => {
          formikHelpers.setError(undefined);
        })
        .catch((e: yup.ValidationError) => {
          formikHelpers.setError(e.message);
          if (field.requireSubmit) {
            formikHelpers.setTouched(true, false); // required to show errors in precursor
          }
        });
    }
    if (!d) {
      return;
    }
    formikHelpers.setTouched(true, false);
    const strValue = getISODateOrEmptyString(d);
    setControlledValue(strValue);
    if (!field.requireSubmit) {
      setTimeout(() => {
        handleFieldSubmit(strValue);
      }, 1000);
    }
    if (onBlur && strValue) {
      // hacky hack
      const dummyEvent = {target: {value: strValue}} as any;
      onBlur(dummyEvent);
    }
  };

  const val = field.requireSubmit
    ? controlledValue
      ? moment.utc(controlledValue)
      : null
    : moment.utc(formikField.value);

  return (
    <FormControl
      className={clsx({
        dateSelector: true,
        readOnly,
        editable: !readOnly,
        alwaysShowCalendar:
          (field.type === ActionType.datepicker && field.alwaysShowCalendar) || false,
      })}
      error={error || !!formikMeta.error}
      size={field.size || 'small'}
      fullWidth={field.fullWidth}
    >
      <AutoFieldDebug>{field.id}</AutoFieldDebug>
      {field.type === ActionType.datepicker ? (
        <>
          {!field.alwaysShowCalendar && (
            <DatePicker
              openTo={field.multiyear ? 'year' : undefined}
              slotProps={{
                textField: {
                  onBlur,
                  size: field.size || 'small',
                  variant: 'outlined',
                  fullWidth: field.fullWidth,
                  disabled: field.disabled,
                  error: error || !!formikMeta.error,
                  helperText,
                  inputProps: {
                    'data-test': field.label,
                    readOnly,
                  },
                  InputLabelProps: {
                    shrink: readOnly || undefined,
                  },
                },
              }}
              label={field.label}
              shouldDisableDate={field.shouldDisableDate}
              value={val}
              onChange={onChange}
              format="DD/MM/YYYY"
              readOnly={readOnly}
              minDate={field?.minDate}
              maxDate={field?.maxDate}
              timezone="UTC"
            />
          )}
          {field.alwaysShowCalendar && (
            <StaticDatePicker
              slots={{
                actionBar: () => <></>,
              }}
              slotProps={{}}
              shouldDisableDate={field.shouldDisableDate}
              value={val}
              onChange={onChange}
              readOnly={readOnly}
              minDate={field?.minDate}
              maxDate={field?.maxDate}
              timezone="UTC"
            />
          )}
        </>
      ) : null}
      {field.type === ActionType.datetimepicker ? (
        <DateTimePicker
          slotProps={{
            textField: {
              onBlur,
              size: field.size || 'small',
              variant: 'outlined',
              fullWidth: field.fullWidth,
              disabled: field.disabled,
              error: error || !!formikMeta.error,
              helperText,
              inputProps: {
                'data-test': field.label,
                readOnly,
              },
              InputLabelProps: {
                shrink: readOnly || undefined,
              },
            },
          }}
          label={field.label}
          value={val}
          onChange={onChange}
          readOnly={readOnly}
          format="Do MMMM YYYY h:mm a"
          timezone="UTC"
        />
      ) : null}
      {field.type === ActionType.timepicker ? (
        <StaticTimePicker
          slotProps={{
            actionBar: () => ({actions: []}),
          }}
          value={val}
          onChange={onChange}
          readOnly={readOnly}
          disabled={field.disabled}
          orientation="landscape"
          timezone="UTC"
        />
      ) : null}
      {field.helper ? <FormHelperText>{field.helper}</FormHelperText> : null}
      {formikMeta.touched && formikMeta.error ? (
        <Typography
          color="error"
          variant="body2"
          style={{fontSize: '0.7rem', marginTop: 4}}
        >
          {formikMeta.error}
        </Typography>
      ) : null}
      {/* Only show the confirm button if:
          - it's a precursor type form where we wait for validation before updating formik values (require submit)
          - OR the field is empty and it is valid for it to be empty
          */}
      {field.requireSubmit && !(!field.canBeEmpty && !controlledValue) && (
        <Box mt={3}>
          <Button variant="contained" color="primary" onClick={onSubmit}>
            Confirm
          </Button>
        </Box>
      )}
    </FormControl>
  );
}

export default AutoDateTimePicker;
